import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, UserPlusIcon, SparklesIcon, ChartBarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import axios from 'axios';

const ProjectSettingsModal = ({ isOpen, onClose, project, onProjectUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPrivate: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [originalMembers, setOriginalMembers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [wasManuallyEnhanced, setWasManuallyEnhanced] = useState(false);
  const [error, setError] = useState('');
  const [showMemberSearch, setShowMemberSearch] = useState(false);
  
  // 🚀 NEW: Context bucket state
  const [contextBucket, setContextBucket] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState('');
  const [isRefreshingContext, setIsRefreshingContext] = useState(false);
  
  const searchTimeoutRef = useRef(null);
  const modalRef = useRef(null);

  // 🚀 NEW: Fetch project context bucket
  const fetchContextBucket = async () => {
    if (!project?.id) return;
    
    setContextLoading(true);
    setContextError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/project-context/${project.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setContextBucket(response.data.contextBucket);
    } catch (error) {
      console.error('Error fetching context bucket:', error);
      setContextError('Failed to load context data');
    } finally {
      setContextLoading(false);
    }
  };

  // 🚀 NEW: Update project description in context bucket
  const updateProjectDescriptionInContext = async (description) => {
    if (!project?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/project-context/${project.id}/description`,
        { description, generateEnhanced: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update context bucket state
      setContextBucket(prev => prev ? {
        ...prev,
        projectDescription: response.data.projectDescription,
        enhancedDescription: response.data.enhancedDescription,
        metadata: { ...prev.metadata, totalTokens: response.data.totalTokens }
      } : null);
      
      return response.data;
    } catch (error) {
      console.error('Error updating project description in context:', error);
      // Don't throw - this is supplementary to the main project update
    }
  };

  // 🚀 NEW: Refresh context bucket
  const handleRefreshContext = async () => {
    setIsRefreshingContext(true);
    try {
      await fetchContextBucket();
    } finally {
      setIsRefreshingContext(false);
    }
  };

  // Load project data when modal opens
  useEffect(() => {
    if (isOpen && project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        isPrivate: project.isPrivate || project.settings?.isPrivate || false
      });
      // Set selected members to current project members
      const projectMembers = project.members || [];
      setSelectedMembers(projectMembers);
      setOriginalMembers(projectMembers);
      setError('');
      setShowMemberSearch(false);
      setSearchTerm('');
      setSearchResults([]);
      
      // 🚀 NEW: Fetch context bucket
      fetchContextBucket();
    }
  }, [isOpen, project]);

  // Focus trap for accessibility
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }, [isOpen]);

  // Search for organization members
  const searchMembers = async (term) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/projects/organization/members?search=${encodeURIComponent(term)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Get current user ID from token
      let currentUserId = null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        currentUserId = payload.userId || payload.id;
      } catch (error) {
        console.error('Error decoding token:', error);
      }
      
      // Filter out already selected members and current user
      const selectedIds = selectedMembers.map(m => m.id);
      const filteredResults = response.data.members.filter(member => 
        !selectedIds.includes(member.id) && member.id !== currentUserId
      );
      
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching members:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchMembers(searchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, selectedMembers]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleMemberSelect = (member) => {
    setSelectedMembers(prev => [...prev, member]);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleMemberRemove = (memberId) => {
    // Don't allow removing the project creator
    if (project.createdBy && project.createdBy.id === memberId) {
      setError('Cannot remove the project creator');
      return;
    }
    setSelectedMembers(prev => prev.filter(m => m.id !== memberId));
  };

  const handleEnhanceDescription = async () => {
    if (!formData.description.trim()) {
      setError('Please enter a description to enhance');
      return;
    }

    setIsEnhancing(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/projects/enhance-description`,
        {
          description: formData.description.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFormData(prev => ({
        ...prev,
        description: response.data.enhancedDescription
      }));
    } catch (error) {
      console.error('Error enhancing description:', error);
      setError(error.response?.data?.message || 'Failed to enhance description');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.description.trim()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // 1. First update the basic project details
      const updateResponse = await axios.put(
        `${process.env.REACT_APP_API_URL}/projects/${project.id}`,
        {
          name: formData.name.trim(),
          description: formData.description.trim(),
          isPrivate: formData.isPrivate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 🚀 NEW: Update project description in context bucket
      await updateProjectDescriptionInContext(formData.description.trim());

      // 2. Handle member changes if any
      const originalMemberIds = originalMembers.map(m => m.id);
      const currentMemberIds = selectedMembers.map(m => m.id);
      
      // Find new members to invite (excluding current user and project creator)
      let currentUserId = null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        currentUserId = payload.userId || payload.id;
      } catch (error) {
        console.error('Error decoding token:', error);
      }

      const newMemberIds = currentMemberIds.filter(id => 
        !originalMemberIds.includes(id) && 
        id !== currentUserId &&
        id !== project.createdBy?.id
      );

      // Send invitations for new members
      if (newMemberIds.length > 0) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/projects/${project.id}/members`,
          { memberIds: newMemberIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // TODO: Handle member removal if needed (this would require a separate endpoint)
      // For now, we only handle adding new members

      // 3. Fetch the updated project to get the latest member list
      const projectResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/projects/${project.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onProjectUpdated(projectResponse.data.project);
      onClose();
    } catch (error) {
      console.error('Error updating project:', error);
      setError(error.response?.data?.message || 'Failed to update project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const isFormValid = formData.name.trim() && formData.description.trim();

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onKeyDown={handleKeyDown}
    >
      <div 
        ref={modalRef}
        className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#444444] rounded-3xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden"
        role="dialog"
        aria-labelledby="modal-title"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#444444]">
          <h2 id="modal-title" className="text-xl font-semibold text-gray-900 dark:text-white">
            Project Settings
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Project Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter project name"
                autoComplete="off"
                maxLength={100}
                className="w-full p-4 border border-gray-300 dark:border-[#444444] rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent bg-white dark:bg-[#181818] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                required
              />
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {formData.name.length}/100 characters
              </div>
            </div>

            {/* Project Description */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description *
                </label>
                <button
                  type="button"
                  onClick={handleEnhanceDescription}
                  disabled={isEnhancing || !formData.description.trim()}
                  className="inline-flex items-center px-3 py-1 text-sm text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isEnhancing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-1"></div>
                      Enhancing...
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-4 h-4 mr-1" />
                      Enhance
                    </>
                  )}
                </button>
              </div>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe the project's goals and objectives"
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 dark:border-[#444444] rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent bg-white dark:bg-[#181818] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                required
              />
            </div>

            {/* Privacy Setting */}
            <div className="flex items-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="isPrivate"
                  name="isPrivate"
                  checked={formData.isPrivate}
                  onChange={handleInputChange}
                  className="sr-only peer"
                />
                <div className={`w-[20px] h-[20px] rounded-full border flex items-center justify-center transition-all ${
                  formData.isPrivate 
                    ? 'bg-black dark:bg-white border-black dark:border-white' 
                    : 'bg-white dark:bg-[#282828] border-[#727272] dark:border-[#444444]'
                }`}>
                  {formData.isPrivate && (
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 5L5 9L13 1" stroke={formData.isPrivate ? (document.documentElement.classList.contains('dark') ? 'black' : 'white') : 'none'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </label>
              <label htmlFor="isPrivate" className="ml-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                Make this project private (only invited members can see it)
              </label>
            </div>

            {/* Team Members Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Team Members
                </label>
                <button
                  type="button"
                  onClick={() => setShowMemberSearch(!showMemberSearch)}
                  className="inline-flex items-center px-3 py-1 text-sm text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 font-medium"
                >
                  <UserPlusIcon className="w-4 h-4 mr-1" />
                  Add Members
                </button>
              </div>

              {/* Selected Members */}
              {selectedMembers.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {selectedMembers.map(member => (
                      <div
                        key={member.id}
                        className="relative inline-flex items-center pl-2 pr-4 py-2 bg-gray-100 dark:bg-[#282828] text-gray-800 dark:text-gray-200 rounded-full text-sm"
                      >
                        {member.profileImage?.url ? (
                          <img 
                            src={member.profileImage.url} 
                            alt={member.name}
                            className="w-8 h-8 rounded-full mr-3"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 mr-3">
                            {(() => {
                              if (member.firstName && member.lastName) {
                                return `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
                              } else if (member.name) {
                                const nameParts = member.name.trim().split(' ');
                                if (nameParts.length >= 2) {
                                  return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
                                } else {
                                  return nameParts[0].charAt(0).toUpperCase();
                                }
                              }
                              return 'U';
                            })()}
                          </div>
                        )}
                        <span>{member.name}</span>
                        {/* Show role badge for creator */}
                        {project.createdBy && project.createdBy.id === member.id && (
                          <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                            Creator
                          </span>
                        )}
                        {/* Only show remove button for non-creators */}
                        {(!project.createdBy || project.createdBy.id !== member.id) && (
                          <button
                            type="button"
                            onClick={() => handleMemberRemove(member.id)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-gray-500 hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700 text-white rounded-full flex items-center justify-center text-xs"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Member Search */}
              {showMemberSearch && (
                <div className="space-y-3">
                  {/* Search Input */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search team members by name"
                      autoComplete="new-password"
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-[#444444] rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent bg-white dark:bg-[#181818] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>

                  {/* Results Container */}
                  {(isSearching || (searchTerm && searchResults.length >= 0)) && (
                    <div className="border border-gray-300 dark:border-[#444444] rounded-xl p-4 bg-gray-50 dark:bg-[#181818]">
                      {isSearching && (
                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                          Searching...
                        </div>
                      )}

                      {!isSearching && searchTerm && searchResults.length === 0 && (
                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                          No members found
                        </div>
                      )}

                      {searchResults.length > 0 && (
                        <div className="max-h-48 overflow-y-auto">
                          {searchResults.map(member => (
                            <button
                              key={member.id}
                              type="button"
                              onClick={() => handleMemberSelect(member)}
                              className="w-full flex items-center px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-[#282828] rounded-lg transition-colors"
                            >
                              {member.profileImage?.url ? (
                                <img 
                                  src={member.profileImage.url} 
                                  alt={member.name}
                                  className="w-8 h-8 rounded-full mr-3"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 mr-3">
                                  {(() => {
                                    if (member.firstName && member.lastName) {
                                      return `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
                                    } else if (member.name) {
                                      const nameParts = member.name.trim().split(' ');
                                      if (nameParts.length >= 2) {
                                        return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
                                      } else {
                                        return nameParts[0].charAt(0).toUpperCase();
                                      }
                                    }
                                    return 'U';
                                  })()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {member.name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {member.email}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 🚀 NEW: Context & Memory Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Context & Memory
                </label>
                <button
                  type="button"
                  onClick={handleRefreshContext}
                  disabled={isRefreshingContext}
                  className="inline-flex items-center px-3 py-1 text-sm text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 font-medium disabled:opacity-50"
                >
                  <ArrowPathIcon className={`w-4 h-4 mr-1 ${isRefreshingContext ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
              
              <div className="border border-gray-300 dark:border-[#444444] rounded-lg p-4 bg-gray-50 dark:bg-[#181818]">
                {contextLoading ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 dark:border-white"></div>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading context data...</p>
                  </div>
                ) : contextError ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-red-600 dark:text-red-400 mb-2">{contextError}</p>
                    <button 
                      onClick={fetchContextBucket}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Retry
                    </button>
                  </div>
                ) : contextBucket ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ChartBarIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-white">Project Context</span>
                      </div>
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                        {contextBucket.metadata.totalTokens} tokens
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-white dark:bg-[#222] rounded-lg border border-gray-200 dark:border-[#333]">
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {contextBucket.fileContext.totalFiles}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Files</div>
                      </div>
                      <div className="p-3 bg-white dark:bg-[#222] rounded-lg border border-gray-200 dark:border-[#333]">
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {contextBucket.conversationContext.chatCount}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Conversations</div>
                      </div>
                      <div className="p-3 bg-white dark:bg-[#222] rounded-lg border border-gray-200 dark:border-[#333]">
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {Math.round(contextBucket.metadata.totalTokens / 1000 * 10) / 10}k
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Tokens</div>
                      </div>
                    </div>
                    
                    {contextBucket.conversationContext.lastUpdated && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                        Last updated: {new Date(contextBucket.conversationContext.lastUpdated).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      No context data available yet
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Context will be built as you upload files and have conversations
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-300 rounded-lg text-sm">
                {error}
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-[#444444]">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="inline-flex items-center px-6 py-2 bg-black dark:bg-white hover:bg-gray-100 dark:hover:bg-[#d5d5d5] disabled:bg-gray-400 text-white dark:text-black font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <CheckIcon className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectSettingsModal; 